// SP5 — transaction-scoped eval-context builder + expression validators.
// Consumed by transaction rules, policy `when` clauses, and computed export
// columns wherever a formula needs TXN_SUM/COUNT/MIN/MAX or `transaction.*`.
import { parse } from "./parser/parser.js";
/** The fixed remittance-transaction scalars every eval context carries.
 *  Single source of truth — {@link buildTransactionEvalContext} populates the
 *  context from it, and UI pickers derive their `transaction.*` options from
 *  it, so a future 7th scalar can't silently drift out of the pickers. */
export const TXN_BUILTIN_SCALARS = [
    "ordinal",
    "state",
    "verdict",
    "paymentTotalCents",
    "appliedTotalCents",
    "deltaCents",
];
/** The three transaction-scoped row collections TXN_* aggregates operate over.
 *  Single source of truth — imported by evaluator.ts rather than redeclared,
 *  so a future 4th collection can't silently drift between the two files. */
export const TXN_COLLECTIONS = new Set(["check", "coupon", "allocation"]);
/** Build an {@link EvalContext} scoped to a single remittance transaction:
 *  `transaction.*` resolves scalars + promoted fields; TXN_SUM/COUNT/MIN/MAX
 *  resolve against the check/coupon/allocation row collections. */
export function buildTransactionEvalContext(input) {
    const transaction = { ...input.promotedFields };
    for (const key of TXN_BUILTIN_SCALARS) {
        transaction[key] = input.transaction[key];
    }
    return {
        fields: input.fields ?? {},
        batch: input.batch,
        transaction,
        txnRows: {
            check: input.checks ?? [],
            coupon: input.coupons ?? [],
            allocation: input.allocations ?? [],
        },
    };
}
/** Cheap pre-check (no parse) for whether an expression touches transaction
 *  scope at all — used to decide whether the full transaction context needs
 *  to be built before evaluation. */
export function expressionUsesTxnScope(expr) {
    return /\bTXN_(SUM|COUNT|MIN|MAX)\s*\(/.test(expr) || /\btransaction\./.test(expr);
}
/** Recursively collect every TXN_* functionCall node in the AST, in any
 *  position (top-level, nested inside binary/unary ops, or nested inside
 *  another function's arguments — including filter arguments). */
function collectTxnCalls(node, out) {
    switch (node.type) {
        case "literal":
        case "fieldRef":
            return;
        case "binaryOp":
            collectTxnCalls(node.left, out);
            collectTxnCalls(node.right, out);
            return;
        case "unaryOp":
            collectTxnCalls(node.operand, out);
            return;
        case "functionCall":
            if (node.name.startsWith("TXN_")) {
                out.push({ name: node.name, firstArg: node.args[0] });
            }
            for (const arg of node.args)
                collectTxnCalls(arg, out);
            return;
    }
}
/** Validate an expression intended for transaction scope: it must parse, and
 *  every TXN_* call's first argument must be a fieldRef with the shape that
 *  function actually evaluates — `TXN_COUNT` takes a BARE collection name
 *  (check|coupon|allocation); `TXN_SUM`/`TXN_MIN`/`TXN_MAX` take a dotted
 *  `collection.key` ref with a valid collection head. Shape mismatches are
 *  permanently null at runtime, so they're rejected here.
 *  Returns `[]` when valid; otherwise a list of human-readable messages. */
export function validateTransactionExpression(expr) {
    if (!expr || !expr.trim())
        return ["expression is empty"];
    let ast;
    try {
        ast = parse(expr);
    }
    catch (e) {
        return [`parse error: ${e.message}`];
    }
    const errors = [];
    const calls = [];
    collectTxnCalls(ast, calls);
    for (const call of calls) {
        const arg = call.firstArg;
        if (!arg || arg.type !== "fieldRef") {
            errors.push(`${call.name}: expected a collection reference as its first argument`);
            continue;
        }
        const dot = arg.name.indexOf(".");
        const head = dot < 0 ? arg.name : arg.name.slice(0, dot);
        if (!TXN_COLLECTIONS.has(head)) {
            errors.push(`unknown transaction collection '${head}'`);
            continue;
        }
        // Per-function arg shape — mirrors evalTxnAgg's runtime dispatch.
        if (call.name === "TXN_COUNT") {
            if (dot >= 0) {
                errors.push(`TXN_COUNT takes a bare collection name (check|coupon|allocation), got '${arg.name}'`);
            }
        }
        else if (dot < 0) {
            errors.push(`${call.name} requires a collection.key reference (e.g. '${head}.amountCents'), got '${arg.name}'`);
        }
    }
    return errors;
}
//# sourceMappingURL=transaction-context.js.map